import { Button, ButtonIconPosition, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import IconCopy from "@amzn/stencil-react-icons/icons/icon-copy";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import Link from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { NOT_APPLICABLE_STRING } from "common/types/constants";
import { getString } from "common/uistringlabels/uiStringUtils";
import { COUPA_URL, HOP_URL } from "config";
import React from "react";

import { OrderData } from "./OrderDataExtractor";

const STRING_PATH = "myOrders.expandableHardware";
const SIZE_T200 = "T200";
const SHOW_HOP_DETAILS_ARRAY = ["PENDING_SHIPPING", "SHIPPED", "DELIVERED"];
const HOP_ORDER_LINK = "hopOrderLink";
const PO_NUMBER_LINK = "poNumberLink";

interface SectionProps {
  index: number;
  orderData: OrderData;
}

interface LinkConfig {
  url: string;
  extractValue: (value: string) => string;
}

const linkConfigs: Record<string, LinkConfig> = {
  [HOP_ORDER_LINK]: {
    url: `${HOP_URL}/search?logicalOperator=and&text=`,
    extractValue: (value: string) => value,
  },
  [PO_NUMBER_LINK]: {
    url: `${COUPA_URL}/order_headers/`,
    extractValue: (value: string) => value.split("-")[1] ?? value,
  },
};

const DetailField: React.FC<{ label: string; value: string; type?: string }> = ({ label, value, type }) => {
  let content;

  if (value != NOT_APPLICABLE_STRING && type && linkConfigs[type]) {
    const config = linkConfigs[type];
    const { url, extractValue } = config;
    const linkValue = extractValue(value);
    content = (
      <Link
        fontSize={SIZE_T200}
        href={`${url}${linkValue}`}
        target="_blank"
        touchpoint={type}
        icon={<IconExternalLink />}
      >
        {value}
      </Link>
    );
  } else {
    content = <Text fontSize={SIZE_T200}>{value}</Text>;
  }

  return (
    <>
      <Text fontSize={SIZE_T200} color="neutral70">
        {label}
      </Text>
      {content}
    </>
  );
};

const CopyableField: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <Text fontSize={SIZE_T200} color="neutral70">
        {label}
      </Text>
      <Row alignItems="center" gridGap="S300">
        <Text fontSize={SIZE_T200}>{value}</Text>
        <Button
          icon={<IconCopy aria-hidden={true} />}
          iconPosition={ButtonIconPosition.Trailing}
          size={ButtonSize.Small}
          variant={ButtonVariant.Tertiary}
          onClick={() => copyToClipboard(value)}
        >
          {getString(`${STRING_PATH}.copyButtonLabel`)}
        </Button>
      </Row>
    </>
  );
};

export const OrderDetailsSection: React.FC<SectionProps> = ({ index, orderData }) => {
  const {
    orderVersion,
    orderPrice,
    costCenter,
    businessJustification,
    carCode,
    serialNumber,
    securityTapeSerialNumber,
    assetTag,
    poNumber,
  } = orderData;
  const showHopDetails = SHOW_HOP_DETAILS_ARRAY.includes(orderData.orderStatus);
  return (
    <Col key={`orderDetails_${index}`} gridGap="S100" style={{ flex: 1 }}>
      <Text fontSize={SIZE_T200} fontWeight="bold" color="neutral90">
        {getString(`${STRING_PATH}.orderDetails`)}
      </Text>
      <DetailField label={getString(`${STRING_PATH}.version`)} value={orderVersion} />
      <DetailField label={getString(`${STRING_PATH}.price`)} value={orderPrice} />
      {showHopDetails && (
        <>
          <DetailField label={getString(`${STRING_PATH}.serialNumber`)} value={serialNumber} />
          <DetailField label={getString(`${STRING_PATH}.assetTag`)} value={assetTag} />
          <DetailField label={getString(`${STRING_PATH}.securityTapeSerialNumber`)} value={securityTapeSerialNumber} />
        </>
      )}
      <DetailField label={getString(`${STRING_PATH}.costCenter`)} value={costCenter} />
      <DetailField label={getString(`${STRING_PATH}.carCode`)} value={carCode} />
      <DetailField label={getString(`${STRING_PATH}.businessJustification`)} value={businessJustification} />
      <DetailField label={getString(`${STRING_PATH}.poNumber`)} value={poNumber} type={PO_NUMBER_LINK} />
      <DetailField
        label={getString(`${STRING_PATH}.returnedDeviceSerialNumber`)}
        value={orderData.returnedDeviceSerialNumber}
      />
    </Col>
  );
};

export const ShippingDetailsSection: React.FC<SectionProps> = ({ index, orderData }) => {
  const { requesterName, shippingAddress, phoneNumber, trackingNumber, shippingCarrier, hopId } = orderData;

  return (
    <Col key={`shippingDetails_${index}`} gridGap="S100" style={{ flex: 1 }}>
      <Text fontSize={SIZE_T200} fontWeight="bold" color="neutral90">
        {getString(`${STRING_PATH}.shippingDetails`)}
      </Text>
      <DetailField label={getString(`${STRING_PATH}.recipient`)} value={requesterName} />
      <DetailField label={getString(`${STRING_PATH}.shippingAddress`)} value={shippingAddress} />
      <DetailField label={getString(`${STRING_PATH}.deliveryInstructions`)} value={orderData.deliveryInstructions} />
      <DetailField label={getString(`${STRING_PATH}.phoneNumber`)} value={phoneNumber} />
      <DetailField label={getString(`${STRING_PATH}.carrier`)} value={shippingCarrier} />
      <DetailField label={getString(`${STRING_PATH}.trackingNumber`)} value={trackingNumber} />
      <DetailField label={getString(`${STRING_PATH}.shippingOrderId`)} value={hopId} type={HOP_ORDER_LINK} />
    </Col>
  );
};

export const HardwareDetailsSection: React.FC<SectionProps> = ({ index, orderData }) => {
  const { serialNumber, assetTag } = orderData;

  return (
    <Col key={`hardwareDetails_${index}`} gridGap="S100" style={{ flex: 1 }}>
      <Text fontSize={SIZE_T200} fontWeight="bold" color="neutral90">
        {getString(`${STRING_PATH}.hardwareDetails`)}
      </Text>
      <CopyableField label={getString(`${STRING_PATH}.serialNumber`)} value={serialNumber} />
      <CopyableField label={getString(`${STRING_PATH}.assetTag`)} value={assetTag} />
    </Col>
  );
};
